import Popover from "common/Popover/Popover.tsx";
import React, { Dispatch, SetStateAction, useMemo } from "react";
import { OperationHoursEvent } from "@shared/types";
import Flex from "common/Boxes/Flex.tsx";
import Text from "common/Typography/Text.tsx";
import CloseOutlined from "@ant-design/icons/CloseOutlined";
import Title from "common/Typography/Title.tsx";
import { WEEK_DAY_TO_DISPLAY_NAME } from "common/Calendars/constants.ts";
import { getIsAllDayEvent } from "common/Calendars/utils.ts";
import dayjs from "dayjs";
import styled from "styled-components";
import Link from "common/Typography/Link.tsx";
import DeleteOutlined from "@ant-design/icons/DeleteOutlined";
import Tooltip from "common/Tooltips/Tooltip.tsx";
import useEventPopover from "common/Calendars/useEventPopover.ts";
import { TimePicker } from "antd";

interface EventPopoverProps {
  popoverTarget: { right: number; top: number } | null;
  selectedEvent: OperationHoursEvent | null;
  setSelectedEvent: (selectedEvent: OperationHoursEvent | null) => void;
  showPopover: boolean;
  setShowPopover: (showPopover: boolean) => void;
  popoverTitle?: string;
  setMyEvents?: Dispatch<SetStateAction<OperationHoursEvent[] | undefined>>;
  viewOnly?: boolean;
  handleDeleteEvent?: () => void;
}

const EventPopover = ({
  showPopover,
  setShowPopover,
  popoverTitle,
  popoverTarget,
  selectedEvent,
  setSelectedEvent,
  setMyEvents,
  viewOnly,
  handleDeleteEvent,
}: EventPopoverProps) => {
  const {
    onAllDayClick,
    handlePopoverClose,
    editEvent,
    isBottomNavigator,
    isSmallScreen,
    timePickerProps,
    isTimePickerOpen,
    setIsTimePickerOpen,
  } = useEventPopover({
    showPopover,
    setShowPopover,
    selectedEvent,
    setSelectedEvent,
    setMyEvents,
  });

  const popoverContentWidth = isBottomNavigator
    ? MOBILE_POPOVER_WIDTH
    : POPOVER_WIDTH;
  const popoverWidth = popoverContentWidth + 12 * 2;
  const popoverContent = selectedEvent && (
    <div style={{ width: popoverContentWidth }}>
      <Flex style={{ marginBottom: 12 }}>
        <Text type="medium">{popoverTitle}</Text>
        <Flex>
          {!viewOnly && (
            <Tooltip title="Delete Event">
              <DeleteIcon onClick={handleDeleteEvent} />
            </Tooltip>
          )}
          <CloseIcon onClick={handlePopoverClose} />
        </Flex>
      </Flex>
      <Flex>
        {selectedEvent.start && (
          <Title level={3}>
            {WEEK_DAY_TO_DISPLAY_NAME[selectedEvent.start.getDay()]}
          </Title>
        )}
        {!viewOnly && (
          <StyledLink
            onClick={onAllDayClick}
            $disabled={getIsAllDayEvent(selectedEvent)}
          >
            All Day
          </StyledLink>
        )}
      </Flex>
      <TimePicker.RangePicker
        style={{ width: "100%", marginTop: 12 }}
        defaultValue={[dayjs(selectedEvent.start), dayjs(selectedEvent.end)]}
        onChange={editEvent}
        disabled={viewOnly}
        onOpenChange={(open) => setIsTimePickerOpen(open)}
        {...timePickerProps}
      />
    </div>
  );

  const popoverTop = useMemo(() => {
    if (!popoverTarget) return;
    if (popoverTarget.top + POPOVER_HEIGHT > window.innerHeight) {
      return window.innerHeight - 20;
    } else if (popoverTarget.top < 0) {
      return 16;
    }
    const isExceedHorizontal =
      popoverTarget.right + popoverWidth > window.innerWidth;
    return (
      popoverTarget.top + (isSmallScreen || isExceedHorizontal ? 155 : 120)
    );
  }, [popoverTarget, isSmallScreen]);

  const popoverLeft = useMemo(() => {
    if (!popoverTarget) return;
    if (
      !isSmallScreen &&
      popoverTarget.right + popoverWidth > window.innerWidth
    ) {
      return window.innerWidth - 32;
    }
    return popoverTarget.right + (isSmallScreen ? 0 : 175);
  }, [popoverTarget, isSmallScreen]);

  return (
    popoverTarget &&
    selectedEvent && (
      <Popover
        trigger="click"
        open={showPopover}
        onOpenChange={(open) => {
          if (!open && !isTimePickerOpen) {
            setTimeout(() => setShowPopover(false), 100);
            setSelectedEvent(null);
          }
        }}
        content={popoverContent}
        arrow={false}
        styles={{ root: { height: POPOVER_HEIGHT, width: popoverWidth } }}
        classNames={{ root: "border" }}
        zIndex={2000}
      >
        <div
          style={{
            position: "fixed",
            top: popoverTop,
            left: popoverLeft,
            width: 1,
            height: 1,
          }}
        />
      </Popover>
    )
  );
};

export default EventPopover;

const POPOVER_HEIGHT = 130;
const POPOVER_WIDTH = 300;
const MOBILE_POPOVER_WIDTH = 220;

const StyledLink = styled(Link)<{ $disabled?: boolean }>`
  ${(p) =>
    p.$disabled &&
    `
    pointer-events: none;
    color: ${p.theme.colors.disabledText} !important;
    &:hover {
       color: ${p.theme.colors.disabledText} !important;
    }
  `};
`;
const DeleteIcon = styled(DeleteOutlined)`
  margin-right: 12px;
  color: ${(p) => p.theme.colors.mediumGrey};
`;
const CloseIcon = styled(CloseOutlined)`
  color: ${(p) => p.theme.colors.mediumGrey};
`;
